import { Modal, type ModalHandle, type ModalProps } from '@lfg/ui/components';
import { Stack, Typography } from '@mui/material';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { useSwitchChain } from 'wagmi';

import { colors } from 'src/global/styles/colors';
import { useWeb3 } from 'src/hooks/useWeb3';

import { PrimaryButton } from './Buttons/PrimaryButton';

export interface SwitchNetworkHandle extends ModalHandle {}
export interface SwitchNetworkModalProps extends Omit<ModalProps, 'children'> {}

const SwitchNetworkModal = forwardRef<SwitchNetworkHandle, SwitchNetworkModalProps>(({ ...rest }, ref) => {
  const modalRef = useRef<ModalHandle>(null);
  const { chain, chainConfig, targetChainId } = useWeb3();
  const { isPending, switchChainAsync } = useSwitchChain();
  const onShow = useCallback(() => modalRef.current?.show(), [modalRef.current]);
  const onHide = useCallback(() => modalRef.current?.hide(), [modalRef.current]);

  useImperativeHandle(ref, () => ({ show: onShow, hide: onHide }), [onShow, onHide]);

  const onSwitchChain = async () => {
    if (!targetChainId) return;
    await switchChainAsync({ chainId: targetChainId });
    onHide();
  };

  return (
    <Modal ref={modalRef} onClose={onHide} bgColor={colors.modal} {...rest} disableScrollLock>
      <Stack gap={3} flexGrow={1}>
        <Stack gap={2} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
          <Typography component={'h3'} fontSize={{ xs: '16px', sm: '18px' }} fontWeight={'600'} color={'white'}>
            Switch Network
          </Typography>
        </Stack>
        <Typography component={'span'} fontSize={{ xs: '14px', sm: '16px' }} color={'white'} lineHeight={'1.5rem'}>
          We do not support {chain?.name} at the moment. To continue using our platform, please switch to{' '}
          <Typography component={'span'} alignItems={'center'}>
            <Typography component={'span'} fontWeight={'600'}>
              {chainConfig.name}
            </Typography>
          </Typography>
        </Typography>
        <Stack width={'100%'} alignItems={'center'}>
          <PrimaryButton isFullWidth disabled={isPending} isLoading={isPending} onClick={onSwitchChain}>
            Switch Network
          </PrimaryButton>
        </Stack>
      </Stack>
    </Modal>
  );
});

export default SwitchNetworkModal;
